import { Visibility } from '@mui/icons-material';
import { Grid, IconButton, useEventCallback } from '@mui/material';
import { BodyPracticeStateVM } from 'practice/view-models/practice-detail';
import { Fragment, memo, ReactElement, useMemo, useState } from 'react';
import Table from 'shell/components/shared/table';
import { HeadCell } from 'shell/components/shared/table/types';
import PmiTextField from 'shell/components/shared/text-field';
import { noop } from 'shell/utils/noop';

interface TablePracticeStateProps {
  data?: Array<BodyPracticeStateVM>;
  disabled?: boolean;
  onEditPress?: (item: BodyPracticeStateVM) => void;
  onViewPress?: (item: BodyPracticeStateVM) => void;
  onDeletePress?: (item: BodyPracticeStateVM) => void;
  hideButtons?: boolean;
}

const headCellsAction: HeadCell<BodyPracticeStateVM, keyof BodyPracticeStateVM>[] = [
  {
    id: 'nr',
    label: 'NR',
  },
  {
    id: 'dateOperation',
    label: 'Data',
  },
  {
    id: 'operation',
    label: 'Operazione',
  },
  {
    id: 'cancellation',
    label: 'Cancellazione',
  },
  {
    id: 'service',
    label: 'Servizio',
  },
  {
    id: 'operator',
    label: 'Operatore',
  },
  {
    id: 'action',
    label: 'Azioni',
  },
];

const headCells: HeadCell<BodyPracticeStateVM, keyof BodyPracticeStateVM>[] = [
  {
    id: 'nr',
    label: 'NR',
  },
  {
    id: 'dateOperation',
    label: 'Data',
  },
  {
    id: 'operation',
    label: 'Operazione',
  },
  {
    id: 'service',
    label: 'Servizio',
  },
  {
    id: 'operator',
    label: 'Operatore',
  },
];

const TablePracticeStateComponent = ({
  data,
  hideButtons = false,
  onViewPress = noop,
}: // disabled,
TablePracticeStateProps): ReactElement => {
  const [elementToVisualize, setElementToVisualize] = useState<BodyPracticeStateVM | null>(null);

  const [tableProps, setTableProps] = useState({
    page: 0,
    rowsPerPage: 10,
  });

  const body: Array<BodyPracticeStateVM> = useMemo(() => {
    return (data || []).map((item) => {
      return {
        ...item,
        action:
          hideButtons || !Boolean(item.note) ? undefined : (
            <Fragment>
              <IconButton
                // disabled={disabled}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setElementToVisualize(elementToVisualize?.nr === item.nr ? null : item);
                  onViewPress(item);
                }}
                aria-label="Visualizza elemento"
                aria-haspopup="true"
                style={{
                  padding: '4px',
                  border: elementToVisualize?.nr === item.nr ? '1px solid black' : 'none',
                  borderRadius: '50%',
                }}
              >
                <Visibility />
              </IconButton>
            </Fragment>
          ),
      };
    });
  }, [data, setElementToVisualize, elementToVisualize]);

  const onChangePage = useEventCallback((page: number) => {
    setTableProps({
      page,
      rowsPerPage: tableProps.rowsPerPage,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setTableProps({
      page: 0,
      rowsPerPage,
    });
  });

  const headCellsCalculated = useMemo(() => {
    const hasActions = body.find((item) => item.action !== undefined);

    if (hasActions) {
      return headCellsAction;
    }

    return headCells;
  }, [body]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {body?.length > 0 ? (
          <Table
            ariaLabel="Lista stati pratiche"
            page={tableProps.page || 0}
            rowsPerPage={tableProps.rowsPerPage || 10}
            body={body}
            count={body?.length || 0}
            head={headCellsCalculated}
            onSort={noop}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            orders={{}}
            isNotLayoutFixed
            noSort
            hideButtons
          />
        ) : (
          'Nessuna informazione'
        )}
      </Grid>
      {elementToVisualize ? (
        <Grid item xs={12}>
          <PmiTextField
            aria-label="Note"
            label="Note"
            id="note"
            name="note"
            fullWidth
            disabled
            type="text"
            variant="outlined"
            multiline
            rows={10}
            value={elementToVisualize?.note || ''}
            onChange={noop}
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

TablePracticeStateComponent.displayName = 'Practice Research - Table Persons component';

const TablePracticeState = memo(TablePracticeStateComponent);

export default TablePracticeState;
